import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lightbulb, Bug, ChevronDown, ChevronUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatJamaicaDate as formatDate } from "@/lib/datetime";

interface FeedbackRow {
  id: string;
  type: "suggestion" | "bug_report";
  subject: string;
  message: string;
  status: "new" | "reviewed" | "done";
  createdAt: string;
  firstName: string;
  lastName: string;
  grade: number;
  school: string | null;
}

interface FeedbackResponse {
  rows: FeedbackRow[];
  total: number;
  page: number;
  pages: number;
}

const STATUS_COLORS: Record<string, string> = {
  new: "bg-blue-500",
  reviewed: "bg-yellow-500",
  done: "bg-green-500",
};

export default function AdminFeedback() {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expanded, setExpanded] = useState<string | null>(null);

  const params = new URLSearchParams({ page: String(page) });
  if (typeFilter !== "all") params.set("type", typeFilter);
  if (statusFilter !== "all") params.set("status", statusFilter);

  const { data, isLoading } = useQuery<FeedbackResponse>({
    queryKey: ["/api/admin/feedback", page, typeFilter, statusFilter],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/feedback?${params}`);
      return res.json();
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      await apiRequest("PATCH", `/api/admin/feedback/${id}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/feedback"] });
      toast({ title: "Status updated" });
    },
    onError: () => {
      toast({ title: "Failed to update status", variant: "destructive" });
    },
  });

  function handleFilterChange() {
    setPage(1);
    setExpanded(null);
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">User Feedback</h1>
        <p className="text-muted-foreground text-sm mt-1">Suggestions and bug reports submitted by students</p>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <Select
          value={typeFilter}
          onValueChange={(v) => { setTypeFilter(v); handleFilterChange(); }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="suggestion">Suggestions</SelectItem>
            <SelectItem value="bug_report">Bug Reports</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={statusFilter}
          onValueChange={(v) => { setStatusFilter(v); handleFilterChange(); }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="done">Done</SelectItem>
          </SelectContent>
        </Select>
        {data && (
          <span className="text-sm text-muted-foreground self-center">{data.total} item{data.total !== 1 ? "s" : ""}</span>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : !data?.rows.length ? (
            <div className="p-8 text-center text-muted-foreground">No feedback found.</div>
          ) : (
            <div className="divide-y divide-border">
              {data.rows.map((row) => (
                <div key={row.id} className="p-4">
                  <div
                    className="flex items-start gap-3 cursor-pointer"
                    onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                  >
                    {/* Icon */}
                    <div className={`mt-0.5 flex-shrink-0 p-1.5 rounded-full ${row.type === "suggestion" ? "bg-yellow-100 dark:bg-yellow-900/30" : "bg-red-100 dark:bg-red-900/30"}`}>
                      {row.type === "suggestion"
                        ? <Lightbulb className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />
                        : <Bug className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />}
                    </div>

                    {/* Main content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm">{row.subject}</p>
                        <Badge className={`${STATUS_COLORS[row.status]} text-white text-xs`}>
                          {row.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {row.firstName} {row.lastName} · Grade {row.grade}{row.school ? ` · ${row.school}` : ""} · {formatDate(row.createdAt)}
                      </p>
                      {expanded !== row.id && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{row.message}</p>
                      )}
                    </div>

                    {/* Expand toggle */}
                    <div className="flex-shrink-0 text-muted-foreground">
                      {expanded === row.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </div>

                  {/* Expanded view */}
                  {expanded === row.id && (
                    <div className="mt-3 ml-9 space-y-3">
                      <p className="text-sm whitespace-pre-wrap">{row.message}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Status:</span>
                        {(["new", "reviewed", "done"] as const).map((s) => (
                          <Button
                            key={s}
                            size="sm"
                            variant={row.status === s ? "default" : "outline"}
                            className="h-7 text-xs capitalize"
                            onClick={() => statusMutation.mutate({ id: row.id, status: s })}
                            disabled={statusMutation.isPending}
                          >
                            {s}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">Page {page} of {data.pages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page === data.pages}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
